import { chartPeriods } from "app/charts/types";
import React from "react";
import { FrontendStore } from "app/main/functions";
import { DataProviderContext } from "app/main/DataProvider";
import { getChartDataUrl } from "app/main/urls";

/**
 * [data,loading,error,needsFetch]
 * data: [data: [number, number][], performance: number, todayExchangeOpen: string]
 */
export type chartDataHookResult = [
    [[number, number][], number | null, string | null | undefined],
    boolean,
    Error | false,
    boolean
];

interface ChartCache {
    [x: string]: chartDataHookResult;
}

enum hookIndex {
    DATA,
    LOADING_STATE,
    ERROR,
    NEED_FETCH
}

/**
 * Fetches chart data and returns along with a nullable error and boolean loading value
 */
export const useChartData = (chartKey: string, period: chartPeriods): chartDataHookResult => {
    const {
        cache,
        getJsonFromUrl,
        config: { apiURL }
    } = React.useContext(DataProviderContext);
    const [update, setUpdate] = React.useState(0);

    const cacheKey = `${chartKey}${period}`;

    let chartCache = (cache.charts || {}) as ChartCache;
    cache.charts = chartCache;

    // chartData, loading, error, doFetchFromNetwork
    let hookResult = chartCache[cacheKey];

    if (!hookResult) {
        hookResult = [[[], null, null], true, false, true];
    }

    if (!chartCache[cacheKey]) {
        chartCache[cacheKey] = hookResult;
    }

    React.useEffect(() => {
        const doFetchFromNetwork = hookResult[hookIndex.NEED_FETCH];
        if (doFetchFromNetwork) {
            hookResult[hookIndex.NEED_FETCH] = false;
            const url = getChartDataUrl(apiURL, chartKey, period);
            getJsonFromUrl(url)
                .then((data: any) => {
                    try {
                        hookResult[hookIndex.LOADING_STATE] = false;
                        // TODO: remove ternary when https://git.intra.hadcs.de/finanz-portal/finance-api/issues/1 has been resolved
                        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                        // @ts-ignore
                        hookResult[hookIndex.DATA] = [data.data, data.performance || null, data.exchangeOpen || null];
                        setUpdate(update + 1);
                    } catch (error) {
                        hookResult[hookIndex.LOADING_STATE] = false;
                        hookResult[hookIndex.ERROR] = error;
                        setUpdate(update + 1);
                    }
                })
                .catch((error: Error) => {
                    hookResult[hookIndex.LOADING_STATE] = false;
                    hookResult[hookIndex.ERROR] = error;
                    setUpdate(update + 1);
                });
        }
    }, [chartCache[cacheKey], update, setUpdate]);

    return hookResult;
};

export function addChartsToStore(
    store: FrontendStore,
    chartKey: string,
    period: string,
    chartData: [number, number][],
    performance: number | null,
    exchangeOpen?: string | null
) {
    const chartStoreObject: chartDataHookResult = [[chartData, performance, exchangeOpen], false, false, false];
    if (store.cache) {
        store.cache.charts = {};
        store.cache.charts[`${chartKey}${period}`] = chartStoreObject;
    }
}
